<template>
  <div class="create-listing-root">
    <head>
      <meta charset="utf-8" />
      <title>FLOCKR | Create Listing</title>
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <meta content="Create a new livestock listing" name="description" />
      <meta content="Flockr" name="author" />
      <link rel="shortcut icon" href="assets/images/favicon.svg">
      <link href="assets/libs/datatables.net-bs4/css/dataTables.bootstrap4.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/libs/datatables.net-buttons-bs4/css/buttons.bootstrap4.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-select@1.13.14/dist/css/bootstrap-select.min.css">
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body :data-sidebar="darkMode ? 'dark' : 'light'">
    <div id="layout-wrapper">

      <!-- ========== TOP BAR ========== -->
      <nav class="cl-topbar">
        <div class="container d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center" style="gap:16px;">
            <a href="javascript:void(0)" @click="$router.push('/')">
              <img src="assets/images/flockr_logo.png" alt="Flockr" style="height:34px;" />
            </a>
            <span class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:15px;">Create Listing</span>
          </div>
          <button class="btn btn-sm btn-outline-secondary" @click="cancelListing">Cancel</button>
        </div>
      </nav>

      <!-- ========== PROGRESS BAR ========== -->
      <div class="cl-progress-wrap">
        <div class="container">
          <div class="cl-progress-bar">
            <div class="cl-progress-fill" :style="'width:' + progressPercent + '%'"></div>
          </div>
          <div class="cl-steps-row">
            <div v-for="(s, si) in steps" :key="si"
                 class="cl-step" :class="{ active: step === si, done: step > si }"
                 @click="goToStep(si)">
              <div class="cl-step-num">
                <i v-if="step > si" class="mdi mdi-check"></i>
                <span v-else>{{ si + 1 }}</span>
              </div>
              <div class="cl-step-label d-none d-md-block">{{ s }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- ========== FORM BODY ========== -->
      <div class="container cl-container">
        <div class="row justify-content-center">
          <div class="col-lg-8">

            <!-- ===== STEP 0: BASIC INFO ===== -->
            <div v-if="step === 0" class="cl-card">
              <h5 class="cl-card-title"><i class="mdi mdi-information-outline me-2"></i>Basic Information</h5>

              <div class="mb-3">
                <label class="cl-label">Listing Type <span class="text-danger">*</span></label>
                <div class="d-flex gap-2 flex-wrap">
                  <button v-for="t in listingTypes" :key="t.value" class="cl-type-btn" :class="{ active: form.listingType === t.value }" @click="form.listingType = t.value">
                    <i :class="t.icon" class="me-1"></i> {{ t.label }}
                  </button>
                </div>
              </div>

              <div class="mb-3">
                <label class="cl-label">Species <span class="text-danger">*</span></label>
                <select v-model="form.speciesId" class="form-select cl-select" @change="onSpeciesChange">
                  <option value="">Select species...</option>
                  <option v-for="sp in speciesList" :key="sp.id" :value="sp.id">{{ sp.name }}</option>
                </select>
              </div>

              <div class="mb-3" v-if="breedsList.length > 0">
                <label class="cl-label">Breed</label>
                <select v-model="form.breedId" class="form-select cl-select">
                  <option value="">Select breed (optional)...</option>
                  <option v-for="br in breedsList" :key="br.id" :value="br.id">{{ br.name }}</option>
                </select>
              </div>

              <div class="mb-3">
                <label class="cl-label">Title <span class="text-danger">*</span></label>
                <input v-model="form.title" type="text" class="form-control cl-input" placeholder="e.g. Healthy Friesian Heifer - 18 months" maxlength="300" />
                <div class="cl-hint">{{ form.title.length }}/300 characters</div>
              </div>

              <div class="mb-3">
                <label class="cl-label">Description <span class="text-danger">*</span></label>
                <textarea v-model="form.description" class="form-control cl-input" rows="4" placeholder="Describe the animal(s) — health, temperament, feeding, reason for sale..."></textarea>
              </div>

              <div class="mb-3" v-if="farms.length > 0">
                <label class="cl-label">Farm (optional)</label>
                <select v-model="form.farmId" class="form-select cl-select">
                  <option value="">Select farm...</option>
                  <option v-for="f in farms" :key="f.id" :value="f.id">{{ f.name }}</option>
                </select>
              </div>
            </div>

            <!-- ===== STEP 1: ANIMAL DETAILS ===== -->
            <div v-if="step === 1" class="cl-card">
              <h5 class="cl-card-title"><i class="mdi mdi-paw me-2"></i>Animal Details</h5>

              <div v-if="form.listingType === 'individual'" class="row g-3">
                <div class="col-md-4">
                  <label class="cl-label">Sex</label>
                  <select v-model="form.sex" class="form-select cl-select">
                    <option value="">Unknown</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
                <div class="col-md-4">
                  <label class="cl-label">Age (months)</label>
                  <input v-model="form.ageMonths" type="number" class="form-control cl-input" min="0" placeholder="e.g. 18" />
                </div>
                <div class="col-md-4">
                  <label class="cl-label">Weight (kg)</label>
                  <input v-model="form.weightKg" type="number" step="0.1" class="form-control cl-input" min="0" placeholder="e.g. 350" />
                </div>
              </div>

              <div v-else class="row g-3">
                <div class="col-md-4">
                  <label class="cl-label">Total Quantity <span class="text-danger">*</span></label>
                  <input v-model="form.totalQuantity" type="number" class="form-control cl-input" min="1" placeholder="e.g. 20" />
                </div>
                <div class="col-md-4">
                  <label class="cl-label">Available Quantity</label>
                  <input v-model="form.availableQuantity" type="number" class="form-control cl-input" min="1" placeholder="e.g. 20" />
                </div>
                <div class="col-md-4">
                  <label class="cl-label">Min Order Quantity</label>
                  <input v-model="form.minOrderQuantity" type="number" class="form-control cl-input" min="1" placeholder="1" />
                </div>
                <div class="col-md-6">
                  <label class="cl-label">Average Weight (kg)</label>
                  <input v-model="form.avgWeightKg" type="number" step="0.1" class="form-control cl-input" min="0" placeholder="e.g. 35" />
                </div>
                <div class="col-md-6">
                  <label class="cl-label">Sex</label>
                  <select v-model="form.sex" class="form-select cl-select">
                    <option value="">Mixed / Unknown</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
              </div>

              <div class="mt-3 p-3" style="background:var(--fm-bg2,#0F0F0F);border-radius:8px;border:1px solid var(--fm-br,#1E1E1E);">
                <div class="d-flex align-items-center gap-2 mb-2" style="color:#BFA164;font-size:13px;">
                  <i class="mdi mdi-lightbulb-outline"></i> <strong>Tip</strong>
                </div>
                <p style="color:var(--fm-m,#A09888);font-size:12px;margin:0;">
                  Listings with weight and age details get 2x more inquiries. Buyers value transparency!
                </p>
              </div>
            </div>

            <!-- ===== STEP 2: PRICING ===== -->
            <div v-if="step === 2" class="cl-card">
              <h5 class="cl-card-title"><i class="mdi mdi-cash me-2"></i>Pricing</h5>

              <div class="mb-3">
                <label class="cl-label">Price (KES) <span class="text-danger">*</span></label>
                <div class="input-group">
                  <span class="input-group-text" style="background:var(--fm-bg3,#1E1E1E);border-color:#2A2520;color:#BFA164;font-weight:700;">KES</span>
                  <input v-model="form.price" type="number" step="0.01" class="form-control cl-input" min="0" placeholder="0.00" style="border-left:none;" />
                </div>
                <div class="cl-hint" v-if="form.listingType !== 'individual' && form.totalQuantity > 1">
                  Total value: KES {{ formatPrice(form.price * (form.totalQuantity || 1)) }} for {{ form.totalQuantity }} animals
                </div>
              </div>

              <div class="mb-3">
                <label class="cl-label">Price Type</label>
                <div class="d-flex gap-2 flex-wrap">
                  <button v-for="pt in priceTypes" :key="pt.value" class="cl-type-btn" :class="{ active: form.priceType === pt.value }" @click="form.priceType = pt.value">
                    {{ pt.label }}
                  </button>
                </div>
              </div>

              <!-- DELIVERY OPTIONS -->
              <div class="cl-delivery-section mt-4" style="padding-top:16px;border-top:1px solid var(--fm-br);">
                <h6 style="color:var(--fm-h,#F5F0E8);font-size:14px;font-weight:600;margin-bottom:12px;"><i class="mdi mdi-truck me-2" style="color:#BFA164;"></i>Delivery Options</h6>
                <div class="d-flex align-items-center justify-content-between mb-3">
                  <div>
                    <div style="color:var(--fm-h,#F5F0E8);font-size:13px;font-weight:600;">I can deliver to buyers</div>
                    <div style="color:var(--fm-s,#6B6356);font-size:11px;">Set delivery fees by distance zone</div>
                  </div>
                  <div class="form-check form-switch">
                    <input class="form-check-input" type="checkbox" v-model="form.offersDelivery" />
                  </div>
                </div>
                <div v-if="form.offersDelivery">
                  <!-- BASE FEE + PER KM -->
                  <div class="cl-zone-grid mb-3">
                    <div class="cl-zone">
                      <div class="cl-zone-header"><i class="mdi mdi-cash" style="color:#50C878;"></i><span>Base Fee</span></div>
                      <div class="cl-zone-desc">Flat charge for loading, handling, etc.</div>
                      <div class="input-group input-group-sm mt-2">
                        <span class="input-group-text" style="background:var(--fm-bg3,#1E1E1E);border-color:#2A2520;color:#BFA164;font-size:11px;">KES</span>
                        <input v-model="form.deliveryBaseFee" type="number" step="100" class="form-control cl-input" min="0" placeholder="e.g. 500" style="border-left:none;font-size:13px;" />
                      </div>
                    </div>
                    <div class="cl-zone">
                      <div class="cl-zone-header"><i class="mdi mdi-map-marker-distance" style="color:#BFA164;"></i><span>Per KM Rate</span></div>
                      <div class="cl-zone-desc">Charge per kilometer of distance</div>
                      <div class="input-group input-group-sm mt-2">
                        <span class="input-group-text" style="background:var(--fm-bg3,#1E1E1E);border-color:#2A2520;color:#BFA164;font-size:11px;">KES</span>
                        <input v-model="form.deliveryPerKm" type="number" step="5" class="form-control cl-input" min="0" placeholder="e.g. 50" style="border-left:none;font-size:13px;" />
                      </div>
                    </div>
                    <div class="cl-zone">
                      <div class="cl-zone-header"><i class="mdi mdi-map-marker-off" style="color:#FF8C00;"></i><span>Max Distance</span></div>
                      <div class="cl-zone-desc">Furthest you'll deliver (KM)</div>
                      <div class="input-group input-group-sm mt-2">
                        <span class="input-group-text" style="background:var(--fm-bg3,#1E1E1E);border-color:#2A2520;color:var(--fm-s,#6B6356);font-size:11px;">KM</span>
                        <input v-model="form.deliveryMaxKm" type="number" step="10" class="form-control cl-input" min="0" placeholder="e.g. 200" style="border-left:none;font-size:13px;" />
                      </div>
                    </div>
                  </div>
                  <div v-if="form.deliveryBaseFee && form.deliveryPerKm" style="background:rgba(80,200,120,0.06);border:1px solid rgba(80,200,120,0.15);border-radius:8px;padding:10px 14px;font-size:12px;color:var(--fm-m,#A09888);margin-bottom:12px;">
                    <i class="mdi mdi-calculator me-1" style="color:#50C878;"></i>
                    <strong style="color:var(--fm-h,#F5F0E8);">Example:</strong>
                    50km = KES {{ formatPrice(Number(form.deliveryBaseFee) + (50 * Number(form.deliveryPerKm))) }} ·
                    100km = KES {{ formatPrice(Number(form.deliveryBaseFee) + (100 * Number(form.deliveryPerKm))) }} ·
                    200km = KES {{ formatPrice(Number(form.deliveryBaseFee) + (200 * Number(form.deliveryPerKm))) }}
                  </div>
                  <div class="cl-hint mb-3">Buyer's fee is calculated automatically: Base fee + (Distance × Per KM rate). Leave max distance blank for unlimited.</div>
                  <div class="mb-3">
                    <label class="cl-label">Delivery Notes (optional)</label>
                    <input v-model="form.deliveryNotes" type="text" class="form-control cl-input" placeholder="e.g. Available Mon-Fri, own truck, 1-2 days" />
                  </div>
                </div>
                <div style="background:rgba(191,161,100,0.08);border:1px solid rgba(191,161,100,0.2);border-radius:8px;padding:10px 14px;font-size:11px;color:var(--fm-m,#A09888);">
                  <i class="mdi mdi-information me-1" style="color:#BFA164;"></i>
                  Buyers can always choose to collect from your location for free. Flockr Transport is also available as a third option.
                </div>
              </div>
            </div>
            <div v-if="step === 3" class="cl-card">
              <h5 class="cl-card-title"><i class="mdi mdi-map-marker me-2"></i>Location</h5>

              <!-- County / sub-county dropdowns removed — listings are now location-pinned via GPS so the platform works worldwide.
                   For Kenyan listings, county_id is still auto-matched from the GPS pin in useMyLocation(). -->
              <div v-if="form.countyId" class="mb-3" style="background:rgba(80,200,120,.06);border:1px solid rgba(80,200,120,.3);border-radius:8px;padding:8px 12px;font-size:11px;color:#50C878;">
                <i class="mdi mdi-check-circle me-1"></i>Detected county: <strong>{{ getCountyName() }}</strong>
              </div>

              <!-- SEARCHABLE LOCATION -->
              <div class="mb-3" style="position:relative;">
                <label class="cl-label"><i class="mdi mdi-crosshairs-gps me-1" style="color:#BFA164;"></i>Pin Exact Location <span class="text-danger">*</span></label>
                <div style="display:flex;gap:8px;">
                  <div style="flex:1;position:relative;">
                    <input v-model="locQuery" type="text" class="form-control cl-input" placeholder="Search town, market, area..." @input="onListingLocSearch" @focus="showListingLocResults = true" autocomplete="off" style="padding-right:36px;" />
                    <i v-if="geoLoading" class="mdi mdi-loading mdi-spin" style="position:absolute;right:12px;top:50%;transform:translateY(-50%);color:var(--fm-s,#6B6356);"></i>
                    <i v-else-if="form.latitude" class="mdi mdi-check-circle" style="position:absolute;right:12px;top:50%;transform:translateY(-50%);color:#50C878;"></i>
                  </div>
                  <button class="cl-gps-btn" @click="useMyLocation" :disabled="geoLoading" title="Use my current location">
                    <i v-if="geoLoading" class="mdi mdi-loading mdi-spin"></i>
                    <i v-else class="mdi mdi-crosshairs-gps"></i>
                  </button>
                </div>
                <div v-if="showListingLocResults && listingLocResults.length > 0" class="cl-loc-dropdown">
                  <div v-for="(loc, li) in listingLocResults" :key="li" class="cl-loc-item" @click="pickListingLocation(loc)">
                    <i class="mdi mdi-map-marker" style="color:#BFA164;flex-shrink:0;margin-top:2px;"></i>
                    <div><div style="font-size:13px;color:var(--fm-h,#F5F0E8);">{{ loc.display_name }}</div><div style="font-size:11px;color:var(--fm-s,#6B6356);">{{ loc.area }}</div></div>
                  </div>
                </div>
              </div>

              <!-- INTERACTIVE MAP -->
              <div class="mb-3">
                <div class="cl-map-container">
                  <div id="listingMap" class="cl-interactive-map"></div>
                  <div class="cl-map-hint"><i class="mdi mdi-gesture-tap me-1"></i>Tap the map to pin animal location</div>
                </div>
                <div v-if="form.latitude && form.longitude" style="font-size:11px;color:#50C878;margin-top:6px;">
                  <i class="mdi mdi-check-circle me-1"></i>{{ form.locationDescription || 'Location set' }}
                  <span style="color:var(--fm-s,#6B6356);">({{ Number(form.latitude).toFixed(4) }}, {{ Number(form.longitude).toFixed(4) }})</span>
                </div>
              </div>

              <div class="mb-3">
                <label class="cl-label">Location Description (optional)</label>
                <input v-model="form.locationDescription" type="text" class="form-control cl-input" placeholder="e.g. Near Meru town, along the Nanyuki highway" />
              </div>
            </div>

            <!-- ===== STEP 4: PHOTOS ===== -->
            <div v-if="step === 4" class="cl-card">
              <h5 class="cl-card-title"><i class="mdi mdi-camera me-2"></i>Photos</h5>
              <p style="color:var(--fm-m,#A09888);font-size:13px;">Upload up to 6 photos. First photo becomes the primary image. Max 2MB each.</p>

              <div class="cl-photo-grid">
                <div v-for="(photo, pi) in photos" :key="'p-'+pi" class="cl-photo-slot">
                  <img :src="photo.preview" :alt="'Photo ' + (pi+1)" />
                  <button class="cl-photo-remove" @click="removePhoto(pi)"><i class="mdi mdi-close"></i></button>
                  <span v-if="pi === 0" class="cl-photo-primary">Primary</span>
                  <span v-if="photo.file" class="cl-photo-size">{{ formatFileSize(photo.file.size) }}</span>
                  <span v-else-if="photo.fromAnimal" class="cl-photo-size" style="background:rgba(80,200,120,.8);">From farm</span>
                </div>
                <label v-if="photos.length < 6 && !compressing" class="cl-photo-add">
                  <input type="file" accept="image/*" multiple @change="handlePhotoUpload" style="display:none;" />
                  <i class="mdi mdi-camera-plus" style="font-size:28px;color:#BFA164;"></i>
                  <span style="font-size:12px;color:var(--fm-s,#6B6356);">Add Photo</span>
                </label>
                <div v-if="compressing" class="cl-photo-add" style="pointer-events:none;">
                  <div class="spinner-border spinner-border-sm text-warning"></div>
                  <span style="font-size:11px;color:#BFA164;">Compressing...</span>
                </div>
              </div>

              <div class="mt-3 p-3" style="background:var(--fm-bg2,#0F0F0F);border-radius:8px;border:1px solid var(--fm-br,#1E1E1E);">
                <div class="d-flex align-items-center gap-2 mb-2" style="color:#BFA164;font-size:13px;">
                  <i class="mdi mdi-lightbulb-outline"></i> <strong>Photo Tips</strong>
                </div>
                <p style="color:var(--fm-m,#A09888);font-size:12px;margin:0;">
                  Take clear, well-lit photos from multiple angles. Show the full body, close-up of the face, and any distinctive markings. Listings with 3+ photos sell 4x faster. Photos are auto-compressed to save data.
                </p>
              </div>
            </div>

            <!-- ===== STEP 5: REVIEW ===== -->
            <div v-if="step === 5" class="cl-card">
              <h5 class="cl-card-title"><i class="mdi mdi-check-circle-outline me-2"></i>Review &amp; Publish</h5>

              <div class="cl-review-section">
                <div class="cl-review-label">Listing Type</div>
                <div class="cl-review-value">{{ form.listingType }}</div>
              </div>
              <div class="cl-review-section">
                <div class="cl-review-label">Title</div>
                <div class="cl-review-value">{{ form.title }}</div>
              </div>
              <div class="cl-review-section">
                <div class="cl-review-label">Species / Breed</div>
                <div class="cl-review-value">{{ getSpeciesName() }} <span v-if="getBreedName()">/ {{ getBreedName() }}</span></div>
              </div>
              <div class="cl-review-section" v-if="form.sex">
                <div class="cl-review-label">Sex</div>
                <div class="cl-review-value">{{ form.sex }}</div>
              </div>
              <div class="cl-review-section" v-if="form.ageMonths">
                <div class="cl-review-label">Age</div>
                <div class="cl-review-value">{{ form.ageMonths }} months</div>
              </div>
              <div class="cl-review-section" v-if="form.weightKg">
                <div class="cl-review-label">Weight</div>
                <div class="cl-review-value">{{ form.weightKg }} kg</div>
              </div>
              <div class="cl-review-section" v-if="form.totalQuantity">
                <div class="cl-review-label">Quantity</div>
                <div class="cl-review-value">{{ form.totalQuantity }}</div>
              </div>
              <div class="cl-review-section">
                <div class="cl-review-label">Price</div>
                <div class="cl-review-value" style="color:#BFA164;font-weight:700;">KES {{ formatPrice(form.price) }} <span style="font-weight:400;color:var(--fm-s,#6B6356);">({{ form.priceType }})</span></div>
              </div>
              <div class="cl-review-section">
                <div class="cl-review-label">Delivery</div>
                <div class="cl-review-value" v-if="form.offersDelivery">Seller delivers — Base: KES {{ formatPrice(form.deliveryBaseFee || 0) }} + KES {{ formatPrice(form.deliveryPerKm || 0) }}/km<span v-if="form.deliveryMaxKm"> · Max {{ form.deliveryMaxKm }}km</span><span v-if="form.deliveryNotes" style="color:var(--fm-s,#6B6356);"> · {{ form.deliveryNotes }}</span></div>
                <div class="cl-review-value" v-else style="color:var(--fm-s,#6B6356);">Buyer collects only</div>
              </div>
              <div class="cl-review-section">
                <div class="cl-review-label">Location</div>
                <div class="cl-review-value">{{ getCountyName() }} <span v-if="form.locationDescription" style="color:var(--fm-s,#6B6356);">— {{ form.locationDescription }}</span></div>
                <div v-if="form.latitude" style="font-size:11px;color:#50C878;margin-top:2px;"><i class="mdi mdi-map-marker-check me-1"></i>Coordinates pinned</div>
              </div>
              <div class="cl-review-section" v-if="photos.length > 0">
                <div class="cl-review-label">Photos</div>
                <div class="d-flex gap-2 flex-wrap">
                  <img v-for="(ph, phi) in photos" :key="'rp-'+phi" :src="ph.preview" style="width:60px;height:60px;object-fit:cover;border-radius:6px;border:1px solid var(--fm-br,#2A2520);" />
                </div>
              </div>

              <div v-if="submitError" class="alert alert-danger mt-3" style="background:#2D1111;border-color:#5C2020;color:#FF6B6B;font-size:13px;">
                <i class="mdi mdi-alert-circle me-1"></i> {{ submitError }}
              </div>
            </div>

            <!-- ===== NAVIGATION BUTTONS ===== -->
            <div class="cl-nav-buttons">
              <button v-if="step > 0" class="btn btn-outline-secondary" @click="prevStep">
                <i class="mdi mdi-arrow-left me-1"></i> Back
              </button>
              <div class="flex-fill"></div>
              <button v-if="step < 5" class="btn btn-warning" @click="nextStep" :disabled="!canAdvance">
                Next <i class="mdi mdi-arrow-right ms-1"></i>
              </button>
              <button v-else class="btn btn-warning" @click="submitListing" :disabled="submitting">
                <span v-if="submitting"><span class="spinner-border spinner-border-sm me-1"></span> Publishing...</span>
                <span v-else><i class="mdi mdi-publish me-1"></i> Publish Listing</span>
              </button>
            </div>

          </div>
        </div>
      </div>

    </div>
    </body>
  </div>
</template>

<!-- ========== STYLES ========== -->
<style>

/* === GLOBAL THEME === */
html[data-theme="light"] body { background:#FFFFFF!important; color:#1A1A1A!important; }
html[data-theme="light"] .fm-card,html[data-theme="light"] .p2c,html[data-theme="light"] .ld-section,html[data-theme="light"] .ld-action-card,html[data-theme="light"] .ld-seller-card,html[data-theme="light"] .ld-info-card,html[data-theme="light"] .ld-related-card { background:#FFFFFF!important; border-color:#E8E2DA!important; }
html[data-theme="light"] .fm-overlay .fm-modal,html[data-theme="light"] .ad-overlay .ad-modal { background:#FFFFFF!important; border-color:#E8E2DA!important; }
html[data-theme="light"] .fm-input,html[data-theme="light"] .fm-select { background:#F7F5F2!important; border-color:#E0D8D0!important; color:#1A1A1A!important; }
html[data-theme="light"] .fm-label { color:#555!important; }
html[data-theme="dark"] body { background:#0A0A0A!important; color:#F5F0E8!important; }
</style>
<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .card{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .card-body{color:#1A1A1A!important}
html[data-theme="light"] .modal-content{background:#FFFFFF!important;border-color:#E8E2DA!important;color:#1A1A1A!important}
html[data-theme="light"] .form-control,html[data-theme="light"] .form-select{background:#FFFFFF!important;border-color:#E0D8D0!important;color:#1A1A1A!important}
html[data-theme="light"] .table{color:#1A1A1A!important}
html[data-theme="light"] .table th{color:#555!important;border-color:#E8E2DA!important}
html[data-theme="light"] .table td{border-color:#F0EBE4!important}
html[data-theme="light"] [style*="color:#F5F0E8"]{color:#1A1A1A!important}
html[data-theme="light"] [style*="color:#6B6356"]{color:#777!important}
html[data-theme="light"] [style*="color:#A09888"]{color:#555!important}
html[data-theme="light"] [style*="background:#0F0F0F"]{background:#F7F5F2!important}
html[data-theme="light"] [style*="background:#141414"]{background:#FFFFFF!important}
html[data-theme="light"] [style*="background:#1E1E1E"]{background:#F0EBE4!important}
html[data-theme="light"] [style*="background:#0A0A0A"]{background:#F7F5F2!important}
html[data-theme="light"] [style*="background:#1a1510"]{background:#FFF8F0!important}
html[data-theme="light"] [style*="background:#1A1A1A"]{background:#F0EBE4!important}
html[data-theme="light"] [style*="border:1px solid #1E1E1E"]{border-color:#E8E2DA!important}
html[data-theme="light"] [style*="border:1px solid #2A2520"]{border-color:#E0D8D0!important}
html[data-theme="light"] [style*="border-bottom:1px solid #1A1A1A"]{border-bottom-color:#F0EBE4!important}
html[data-theme="light"] [style*="border-bottom:1px solid #1E1E1E"]{border-bottom-color:#F0EBE4!important}
html[data-theme="light"] [style*="border-bottom:1px solid #2A2520"]{border-bottom-color:#E0D8D0!important}
html[data-theme="light"] [style*="border-top:1px solid #2A2520"]{border-top-color:#E0D8D0!important}
html[data-theme="light"] [style*="color:#BFA164"]{color:#BFA164!important}
html[data-theme="light"] [style*="color:#50C878"]{color:#50C878!important}
html[data-theme="light"] [style*="color:#4FC3F7"]{color:#4FC3F7!important}
html[data-theme="light"] [style*="color:#FF6B6B"]{color:#FF6B6B!important}
html[data-theme="light"] [style*="color:#FF9800"]{color:#FF9800!important}
html[data-theme="light"] [style*="color:#CE93D8"]{color:#CE93D8!important}
</style>
<style>
html[data-theme="light"] body {
  background:#F7F5F2!important;
  color:#1A1A1A!important;
}

html[data-theme="dark"] body {
  background:#0A0A0A!important;
  color:#F5F0E8!important;
}

html[data-theme="light"] .create-listing-root,
html[data-theme="light"] #layout-wrapper,
html[data-theme="light"] .cl-progress-wrap,
html[data-theme="light"] .cl-container {
  background:#F7F5F2!important;
  color:#1A1A1A!important;
}

html[data-theme="light"] .cl-topbar {
  background:#FFFFFF!important;
  border-bottom-color:#E8E2DA!important;
}

html[data-theme="dark"] .create-listing-root,
html[data-theme="dark"] #layout-wrapper,
html[data-theme="dark"] .cl-progress-wrap,
html[data-theme="dark"] .cl-container {
  background:#0A0A0A!important;
  color:#F5F0E8!important;
}

html[data-theme="dark"] .cl-topbar {
  background:rgba(10,10,10,0.95)!important;
}
</style>

<style scoped>
.create-listing-root { background:var(--fm-bg2,#0F0F0F)!important; min-height:100vh; color:var(--fm-h,#F5F0E8)!important; }

.cl-topbar {
  position:sticky; top:0; z-index:100; padding:12px 0;
  background:var(--fm-bg1,#141414)!important; backdrop-filter:blur(10px);
  border-bottom:1px solid var(--fm-br,#1E1E1E);
}

/* Progress */
.cl-progress-wrap { background:var(--fm-bg2,#0F0F0F)!important; border-bottom:1px solid var(--fm-br,#1E1E1E); padding:16px 0 12px; position:sticky; top:54px; z-index:99; }
.cl-progress-bar { height:4px; background:var(--fm-bg3,#1E1E1E)!important; border-radius:2px; overflow:hidden; margin-bottom:12px; }
.cl-progress-fill { height:100%; background:linear-gradient(90deg, #BFA164, #f1b44c); border-radius:2px; transition:width 0.3s ease; }
.cl-steps-row { display:flex; justify-content:space-between; }
.cl-step { display:flex; align-items:center; gap:6px; cursor:pointer; }
.cl-step-num {
  width:26px; height:26px; border-radius:50%; border:2px solid #2A2520;
  display:flex; align-items:center; justify-content:center;
  font-size:12px; font-weight:700; color:var(--fm-s,#6B6356)!important; background:var(--fm-bg1,#141414)!important;
  transition:all 0.2s ease;
}
.cl-step.active .cl-step-num { border-color:#BFA164; color:#BFA164!important; background:rgba(191,161,100,0.1)!important; }
.cl-step.done .cl-step-num { border-color:#50C878; color:#50C878!important; background:rgba(80,200,120,0.1)!important; }
.cl-step-label { font-size:11px; color:var(--fm-s,#6B6356)!important; }
.cl-step.active .cl-step-label { color:#BFA164!important; font-weight:600; }
.cl-step.done .cl-step-label { color:#50C878!important; }

/* Container */
.cl-container { padding-top:24px; padding-bottom:100px; }

/* Cards */
.cl-card { background:var(--fm-bg1,#141414)!important; border:1px solid var(--fm-br,#1E1E1E); border-radius:12px; padding:24px; margin-bottom:20px; }
.cl-card-title { font-size:16px; font-weight:700; color:var(--fm-h,#F5F0E8)!important; margin-bottom:20px; }

/* Form Elements */
.cl-label { font-size:13px; font-weight:600; color:#D4C9B8!important; margin-bottom:6px; display:block; }
.cl-hint { font-size:11px; color:var(--fm-s,#6B6356)!important; margin-top:4px; }
.cl-zone-grid { display:grid; grid-template-columns:repeat(3, 1fr); gap:10px; }
.cl-zone { background:var(--fm-bg2,#0F0F0F); border:1px solid var(--fm-br,#1E1E1E); border-radius:10px; padding:12px; }
.cl-zone-header { display:flex; align-items:center; gap:6px; font-size:13px; font-weight:600; color:var(--fm-h,#F5F0E8); margin-bottom:2px; }
.cl-zone-desc { font-size:10px; color:var(--fm-s,#6B6356); }
.cl-map-container { position:relative; border-radius:10px; overflow:hidden; border:1px solid var(--fm-br,#2A2520); }
.cl-interactive-map { width:100%; height:250px; background:var(--fm-bg2,#0F0F0F); z-index:1; }
.cl-map-hint { position:absolute; bottom:8px; left:50%; transform:translateX(-50%); background:rgba(10,10,10,0.85); border:1px solid var(--fm-br,#2A2520); border-radius:20px; padding:4px 14px; font-size:11px; color:var(--fm-m,#A09888); z-index:10; pointer-events:none; white-space:nowrap; }
.cl-gps-btn { background:var(--fm-bg3,#1E1E1E); border:1px solid var(--fm-br,#2A2520); color:#BFA164; width:44px; min-width:44px; border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:18px; transition:all .2s; cursor:pointer; }
.cl-gps-btn:hover { background:#2A2520; border-color:#BFA164; }
.cl-gps-btn:disabled { opacity:0.5; cursor:not-allowed; }
.cl-loc-dropdown { position:absolute; top:100%; left:0; right:0; z-index:50; background:var(--fm-bg1,#141414); border:1px solid var(--fm-br,#2A2520); border-radius:8px; margin-top:4px; max-height:220px; overflow-y:auto; box-shadow:0 8px 30px rgba(0,0,0,0.5); }
.cl-loc-item { display:flex; align-items:flex-start; gap:8px; padding:10px 14px; cursor:pointer; transition:background .15s; }
.cl-loc-item:hover { background:rgba(191,161,100,0.08); }
.cl-loc-item:not(:last-child) { border-bottom:1px solid var(--fm-br,#1E1E1E); }
.cl-input {
  background:var(--fm-bg2,#0F0F0F)!important; border:1px solid var(--fm-br,#2A2520); color:var(--fm-h,#F5F0E8)!important; border-radius:8px;
}
.cl-input:focus { border-color:#BFA164; background:var(--fm-bg2,#0F0F0F)!important; color:var(--fm-h,#F5F0E8)!important; box-shadow:0 0 0 2px rgba(191,161,100,0.15); }
.cl-input::placeholder { color:#4A4540!important; }
.cl-select { background:var(--fm-bg2,#0F0F0F)!important; border:1px solid var(--fm-br,#2A2520); color:var(--fm-h,#F5F0E8)!important; border-radius:8px; }
.cl-select:focus { border-color:#BFA164; box-shadow:0 0 0 2px rgba(191,161,100,0.15); }
.cl-select option { background:var(--fm-bg1,#141414)!important; color:var(--fm-h,#F5F0E8)!important; }

/* Type Buttons */
.cl-type-btn {
  padding:8px 16px; border-radius:8px; border:1px solid var(--fm-br,#2A2520);
  background:var(--fm-bg2,#0F0F0F)!important; color:var(--fm-m,#A09888)!important; font-size:13px; cursor:pointer;
  transition:all 0.2s ease;
}
.cl-type-btn:hover { border-color:#BFA164; color:var(--fm-h,#F5F0E8)!important; }
.cl-type-btn.active { background:rgba(191,161,100,0.15)!important; border-color:#BFA164; color:#BFA164!important; font-weight:600; }

/* Photo Grid */
.cl-photo-grid { display:flex; flex-wrap:wrap; gap:12px; }
.cl-photo-slot {
  position:relative; width:110px; height:110px; border-radius:10px;
  overflow:hidden; border:1px solid var(--fm-br,#2A2520);
}
.cl-photo-slot img { width:100%; height:100%; object-fit:cover; }
.cl-photo-remove {
  position:absolute; top:4px; right:4px; width:22px; height:22px;
  border-radius:50%; background:rgba(0,0,0,0.7)!important; border:none;
  color:#fff!important; font-size:12px; cursor:pointer; display:flex;
  align-items:center; justify-content:center;
}
.cl-photo-size {
  position:absolute; bottom:4px; left:4px; background:rgba(0,0,0,0.7);
  color:#50C878; font-size:9px; font-weight:600; padding:1px 5px; border-radius:4px;
}
.cl-photo-primary {
  position:absolute; bottom:0; left:0; right:0; text-align:center;
  background:rgba(191,161,100,0.85)!important; color:#0A0A0A!important; font-size:10px;
  font-weight:700; padding:2px 0;
}
.cl-photo-add {
  width:110px; height:110px; border-radius:10px; border:2px dashed #2A2520;
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  cursor:pointer; transition:all 0.2s ease; gap:4px;
}
.cl-photo-add:hover { border-color:#BFA164; }

/* Review */
.cl-review-section {
  display:flex; justify-content:space-between; align-items:flex-start;
  padding:10px 0; border-bottom:1px solid var(--fm-br,#1A1A1A);
}
.cl-review-section:last-of-type { border-bottom:none; }
.cl-review-label { font-size:13px; color:var(--fm-s,#6B6356)!important; min-width:120px; }
.cl-review-value { font-size:13px; color:var(--fm-h,#F5F0E8)!important; text-align:right; flex:1; }

/* Nav Buttons */
.cl-nav-buttons { display:flex; gap:12px; padding:16px 0; }

@media (max-width: 768px) {
  .cl-container { padding-top:16px; }
  .cl-card { padding:16px; }
  .cl-photo-slot, .cl-photo-add { width:90px; height:90px; }
  .cl-review-section { flex-direction:column; gap:4px; }
  .cl-review-value { text-align:left; }
  .cl-zone-grid { grid-template-columns:1fr; }
}
</style>

<!-- ========== SCRIPT ========== -->
<script>
/* eslint-disable */
import { execute, executeJson, flocker, uploads } from "@/api";

export default {
  name: "CreateListingPage",
  data: function() {
    return {
      darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
      themeObserver: null,

      step: 0,
      steps: ['Basic Info', 'Details', 'Pricing', 'Location', 'Photos', 'Review'],
      submitting: false,
      submitError: '',
      form: {
        listingType: 'individual',
        speciesId: '',
        breedId: '',
        title: '',
        description: '',
        farmId: '',
        animalId: '',
        batchId: '',
        sex: '',
        ageMonths: '',
        weightKg: '',
        totalQuantity: '',
        availableQuantity: '',
        minOrderQuantity: '1',
        avgWeightKg: '',
        price: '',
        priceType: 'negotiable',
        offersDelivery: false,
        deliveryBaseFee: '',
        deliveryPerKm: '',
        deliveryMaxKm: '',
        deliveryNotes: '',
        countyId: '',
        subCountyId: '',
        latitude: '',
        longitude: '',
        locationDescription: ''
      },
      photos: [],
      compressing: false,
      locQuery: '', showListingLocResults: false, listingLocResults: [], locSearchTimer: null, geoLoading: false,
      listingMapInstance: null, listingMarker: null,
      speciesList: [],
      breedsList: [],
      farms: [],
      counties: [],
      subCounties: [],
      listingTypes: [
        { value: 'individual', label: 'Single Animal', icon: 'mdi mdi-paw' },
        { value: 'batch', label: 'Batch / Herd', icon: 'mdi mdi-view-grid-outline' }
      ],
      priceTypes: [
        { value: 'fixed', label: 'Fixed Price' },
        { value: 'negotiable', label: 'Negotiable' },
        { value: 'per_unit', label: 'Per Unit' },
        { value: 'per_kg', label: 'Per Kg' },
        { value: 'auction', label: 'Auction' }
      ]
    };
  },
  computed: {
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    themeCardStyle(){return this.darkMode?'background:#141414!important;border:1px solid #1E1E1E!important;':'background:#FFFFFF!important;border:1px solid #E8E2DA!important;'},
    themeInputStyle(){return this.darkMode?'background:#0F0F0F;border:1px solid #2A2520;color:#F5F0E8;':'background:#FFFFFF;border:1px solid #E0D8D0;color:#1A1A1A;'},

    progressPercent: function() {
      return Math.round(((this.step + 1) / this.steps.length) * 100);
    },
    canAdvance: function() {
      if (this.step === 0) return this.form.speciesId && this.form.title.trim().length >= 5;
      if (this.step === 1) return true;
      if (this.step === 2) return this.form.price > 0;
      if (this.step === 3) return !!this.form.latitude && !!this.form.longitude;
      if (this.step === 4) return true;
      return true;
    }
  },
  watch: {
    step: function(val) {
      if (val === 3) { var self = this; this.$nextTick(function() { self.initListingMap(); }); }
    }
  },
  mounted: function() {
    this.syncTheme();
    this.themeObserver = new MutationObserver(this.syncTheme);
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    var userId = sessionStorage.getItem('userId');
    if (!userId) { this.$router.push('/login'); return; }
    this.fetchSpecies();
    this.fetchCounties();
    this.fetchFarms();

    // Pre-fill from farm animal if animalId passed
    var animalId = this.$route.query.animalId;
    if (animalId) {
      this.prefillFromAnimal(animalId);
    }

    // Pre-fill from batch if batchId passed
    var batchId = this.$route.query.batchId;
    var farmId = this.$route.query.farmId;
    if (batchId) {
      this.prefillFromBatch(batchId, farmId);
    }
  },
  beforeUnmount: function() {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
    if (this.locSearchTimer) {
      clearTimeout(this.locSearchTimer);
    }
  },
  methods: {
    syncTheme: function() {
      var t = document.documentElement.getAttribute('data-theme') || localStorage.getItem('flockr_theme') || 'dark';
      window.__flockrSetTheme(t);
      this.darkMode = t !== 'light';

      if (document.body) {
        document.body.setAttribute('data-sidebar', this.darkMode ? 'dark' : 'light');
        document.body.style.background = this.darkMode ? '#0A0A0A' : '#F7F5F2';
        document.body.style.color = this.darkMode ? '#F5F0E8' : '#1A1A1A';
      }
    },

    // LOCATION SEARCH (Nominatim)
    onListingLocSearch: function() {
      if (this.locSearchTimer) clearTimeout(this.locSearchTimer);
      var self = this; var q = (this.locQuery || '').trim();
      if (q.length < 3) { this.listingLocResults = []; this.showListingLocResults = false; return; }
      this.locSearchTimer = setTimeout(function() { self.doListingLocSearch(q); }, 400);
    },
    doListingLocSearch: async function(q) {
      try {
        // Worldwide search — no country filter so users in Ethiopia, Uganda, Tanzania etc can pin their location
        var url = 'https://nominatim.openstreetmap.org/search?format=json&limit=6&q=' + encodeURIComponent(q);
        var res = await fetch(url, { headers: { 'Accept-Language': 'en' } }); var data = await res.json();
        this.listingLocResults = data.map(function(d) {
          var parts = (d.display_name || '').split(',');
          return { name: parts[0], display_name: parts.slice(0, 3).join(', ').trim(), area: parts.slice(1, 4).join(', ').trim(), lat: d.lat, lon: d.lon };
        });
        this.showListingLocResults = true;
      } catch(e) { this.listingLocResults = []; }
    },
    pickListingLocation: function(loc) {
      this.form.latitude = loc.lat;
      this.form.longitude = loc.lon;
      this.form.locationDescription = loc.display_name || loc.name;
      this.locQuery = loc.display_name || loc.name;
      this.showListingLocResults = false;
      this.updateListingPin(Number(loc.lat), Number(loc.lon));
    },
    useMyLocation: function() {
      if (!navigator.geolocation) return;
      this.geoLoading = true; var self = this;
      navigator.geolocation.getCurrentPosition(
          function(pos) {
            var lat = pos.coords.latitude; var lon = pos.coords.longitude;
            self.form.latitude = lat;
            self.form.longitude = lon;
            self.geoLoading = false;
            self.updateListingPin(lat, lon);
            self.reverseGeocode(lat, lon);
          },
          function() { self.geoLoading = false; },
          { enableHighAccuracy: true, timeout: 10000 }
      );
    },
    reverseGeocode: function(lat, lon) {
      var self = this;
      fetch('https://nominatim.openstreetmap.org/reverse?format=json&lat=' + lat + '&lon=' + lon, { headers: { 'Accept-Language': 'en' } })
          .then(function(r) { return r.json(); })
          .then(function(d) {
            if (d && d.display_name) {
              var parts = d.display_name.split(',');
              self.form.locationDescription = parts.slice(0, 3).join(',').trim();
              self.locQuery = self.form.locationDescription;
            }
            // Auto-match Kenyan county_id silently if the pin lands in Kenya
            if (d && d.address && d.address.country_code === 'ke' && self.counties && self.counties.length) {
              var name = (d.address.county || d.address.state || '').replace(/County$/i, '').trim().toLowerCase();
              if (name) {
                var match = self.counties.find(function(c){ return (c.name||'').toLowerCase() === name; });
                if (match) {
                  self.form.countyId = match.id;
                  // Also try to populate sub-county
                  self.loadSubCounties && self.loadSubCounties();
                }
              }
            } else {
              // Outside Kenya — clear any stale county_id so we don't tag a Mombasa goat as Nairobi
              self.form.countyId = '';
              self.form.subCountyId = '';
            }
          }).catch(function() {});
    },
    loadLeaflet: function(cb) {
      if (window.L) { cb(); return; }
      var css = document.createElement('link');
      css.rel = 'stylesheet'; css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(css);
      var js = document.createElement('script');
      js.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      js.onload = cb;
      document.head.appendChild(js);
    },
    initListingMap: function() {
      var self = this;
      this.loadLeaflet(function() {
        self.$nextTick(function() {
          var el = document.getElementById('listingMap');
          if (!el) return;
          if (self.listingMapInstance) { self.listingMapInstance.invalidateSize(); return; }

          var lat = Number(self.form.latitude) || -1.2921;
          var lon = Number(self.form.longitude) || 36.8219;
          var zoom = self.form.latitude ? 14 : 7;

          self.listingMapInstance = L.map('listingMap', { zoomControl: true, attributionControl: false }).setView([lat, lon], zoom);
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 18 }).addTo(self.listingMapInstance);

          // Click to drop/move pin
          self.listingMapInstance.on('click', function(e) {
            self.form.latitude = e.latlng.lat;
            self.form.longitude = e.latlng.lng;
            self.updateListingPin(e.latlng.lat, e.latlng.lng);
            self.reverseGeocode(e.latlng.lat, e.latlng.lng);
          });

          // If already has coords, show pin
          if (self.form.latitude && self.form.longitude) {
            self.updateListingPin(Number(self.form.latitude), Number(self.form.longitude));
          }
        });
      });
    },
    updateListingPin: function(lat, lon) {
      if (!this.listingMapInstance || !window.L) return;
      var pinIcon = L.divIcon({
        className: 'cl-pin-icon',
        html: '<div style="background:#BFA164;width:16px;height:16px;border-radius:50%;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.5);"></div>',
        iconSize: [16, 16], iconAnchor: [8, 8]
      });
      if (this.listingMarker) {
        this.listingMarker.setLatLng([lat, lon]);
      } else {
        this.listingMarker = L.marker([lat, lon], { icon: pinIcon, draggable: true })
            .addTo(this.listingMapInstance)
            .bindTooltip('Animal location', { permanent: false, direction: 'top' });
        var self = this;
        this.listingMarker.on('dragend', function(e) {
          var pos = e.target.getLatLng();
          self.form.latitude = pos.lat;
          self.form.longitude = pos.lng;
          self.reverseGeocode(pos.lat, pos.lng);
        });
      }
      this.listingMapInstance.setView([lat, lon], Math.max(this.listingMapInstance.getZoom(), 13));
    },

    // ============ NAVIGATION ============

    nextStep: function() {
      if (this.step < 5 && this.canAdvance) this.step++;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    prevStep: function() {
      if (this.step > 0) this.step--;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    goToStep: function(s) {
      if (s <= this.step) this.step = s;
    },
    cancelListing: function() {
      if (this.form.title || this.photos.length > 0) {
        if (!confirm('Discard this listing? Your progress will be lost.')) return;
      }
      this.$router.push('/marketplace');
    },

    // ============ API: REFERENCE DATA ============

    async prefillFromAnimal(animalId) {
      try {
        var f = new FormData();
        f.append('function', 'getAnimalDetail');
        f.append('animalId', animalId);
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data && r.data.data.animal) {
          var a = r.data.data.animal;
          this.form.animalId = a.id;
          this.form.farmId = a.farm_id || '';
          this.form.listingType = 'individual';
          this.form.speciesId = a.species_id || '';
          this.form.breedId = a.breed_id || '';
          this.form.sex = a.sex || '';
          this.form.weightKg = a.weight_kg || '';

          // Calculate age from DOB
          if (a.date_of_birth) {
            var dob = new Date(a.date_of_birth);
            var now = new Date();
            this.form.ageMonths = Math.floor((now - dob) / (30.44 * 86400000));
          } else if (a.estimated_age_months) {
            this.form.ageMonths = a.estimated_age_months;
          }

          // Build title
          var titleParts = [];
          if (a.breed_name) titleParts.push(a.breed_name);
          else if (a.species_name) titleParts.push(a.species_name);
          if (a.sex && a.sex !== 'unknown') titleParts.push(a.sex === 'male' ? 'Bull/Steer' : 'Cow/Heifer');
          if (a.name) titleParts.push('- ' + a.name);
          this.form.title = titleParts.join(' ');

          // Build description with farm records
          var desc = [];
          if (a.name) desc.push(a.name + ' — ' + (a.species_name || '') + (a.breed_name ? ' (' + a.breed_name + ')' : ''));
          if (a.weight_kg) desc.push('Current weight: ' + a.weight_kg + 'kg');
          if (a.health_status === 'healthy') desc.push('Health status: Healthy');
          if (a.color_markings) desc.push('Color/markings: ' + a.color_markings);

          // Add vaccination info
          var vax = r.data.data.animal.vaccinations || [];
          if (vax.length > 0) {
            desc.push('');
            desc.push('Vaccination records:');
            vax.forEach(function(v) {
              desc.push('• ' + v.vaccine_name + ' (' + v.disease_target + ') — ' + (v.administered_date || ''));
            });
          }

          // Add health records summary
          var health = r.data.data.animal.health_records || [];
          if (health.length > 0) {
            desc.push('');
            desc.push('Health history: ' + health.length + ' records on file');
          }

          // Add weight history
          var weights = r.data.data.animal.weight_history || [];
          if (weights.length > 1) {
            var first = weights[weights.length - 1];
            var last = weights[0];
            desc.push('Weight growth: ' + first.weight_kg + 'kg → ' + last.weight_kg + 'kg');
          }

          // Add breeding info
          if (a.reproductive_status && a.reproductive_status !== 'not_applicable') {
            desc.push('Reproductive status: ' + a.reproductive_status.replace(/_/g, ' '));
          }
          if (a.pedigree_number) desc.push('Pedigree #: ' + a.pedigree_number);

          desc.push('');
          desc.push('This animal has verified farm records on Flockr.');
          this.form.description = desc.join('\n');

          // Pre-fill location from farm
          if (a.farm_name) {
            // Try to get farm location
            try {
              var f2 = new FormData();
              f2.append('function', 'getFarmDetail');
              f2.append('farmId', a.farm_id);
              var r2 = await execute(f2, flocker);
              if (r2.data && r2.data.success && r2.data.data && r2.data.data.farm) {
                var farm = r2.data.data.farm;
                if (farm.county_id) this.form.countyId = farm.county_id;
                if (farm.latitude) this.form.latitude = farm.latitude;
                if (farm.longitude) this.form.longitude = farm.longitude;
                if (farm.county_name) this.form.locationDescription = farm.county_name;
              }
            } catch(e2) {}
          }

          // Load breeds for selected species
          if (this.form.speciesId) {
            this.fetchBreeds();
          }

          // Pre-load animal photos into the photos array for display (user can add more)
          var photos = r.data.data.animal.photos || [];
          if (photos.length > 0) {
            var self = this;
            photos.forEach(function(p) {
              if (p.photo_url) {
                self.photos.push({
                  preview: p.photo_url.startsWith('http') ? p.photo_url : (uploads + String(p.photo_url).replace('uploads/', '')),
                  file: null,
                  fromAnimal: true,
                  animalPhotoUrl: p.photo_url
                });
              }
            });
          }
        }
      } catch(e) {
        console.error('Failed to prefill from animal:', e);
      }
    },
    async prefillFromBatch(batchId, farmId) {
      var self = this;
      try {
        var r = await executeJson({function: 'getBatchDetail', batchId: batchId}, flocker);
        if (r.data && r.data.success && r.data.data && r.data.data.batch) {
          var b = r.data.data.batch;
          this.form.batchId = b.id;
          this.form.farmId = farmId || b.farm_id || '';
          this.form.listingType = 'batch';
          this.form.speciesId = b.species_id || '';
          this.form.breedId = b.breed_id || '';
          this.form.sex = b.sex || 'mixed';
          this.form.totalQuantity = b.available_quantity || b.total_quantity || '';
          this.form.availableQuantity = b.available_quantity || b.total_quantity || '';
          this.form.avgWeightKg = b.avg_weight_kg || '';

          // Build title
          var titleParts = [];
          if (b.breed_name) titleParts.push(b.breed_name);
          else if (b.species_name) titleParts.push(b.species_name);
          titleParts.push('Batch — ' + b.batch_name);
          if (b.available_quantity) titleParts.push('(' + b.available_quantity + ' available)');
          this.form.title = titleParts.join(' ');

          // Build description
          var desc = [];
          desc.push(b.batch_name + ' — ' + (b.species_name || '') + (b.breed_name ? ' (' + b.breed_name + ')' : ''));
          desc.push('Quantity: ' + (b.available_quantity || b.total_quantity) + ' animals');
          if (b.avg_weight_kg) desc.push('Average weight: ' + b.avg_weight_kg + 'kg');
          if (b.sex && b.sex !== 'mixed') desc.push('Sex: ' + b.sex);
          if (b.health_status) desc.push('Health status: ' + b.health_status);

          // Add vaccination info from batch detail
          var vax = r.data.data.vaccinations || [];
          if (vax.length > 0) {
            desc.push('');
            desc.push('Vaccination records:');
            vax.forEach(function(v) {
              desc.push('• ' + v.vaccine_name + ' (' + v.disease_target + ') — ' + (v.administered_date || ''));
            });
          }

          // Health summary
          var health = r.data.data.healthRecords || [];
          if (health.length > 0) {
            desc.push('');
            desc.push('Health history: ' + health.length + ' records on file');
          }

          // Cost info
          var costs = r.data.data.costs || {};
          if (costs.total > 0) {
            desc.push('');
            desc.push('Total investment: KES ' + Math.round(costs.total).toLocaleString());
          }

          desc.push('');
          desc.push('This batch has verified farm records on Flockr.');
          this.form.description = desc.join('\n');

          // Load breeds
          if (this.form.speciesId) this.fetchBreeds();

          // Pre-fill farm location
          if (this.form.farmId) {
            try {
              var f2 = new FormData();
              f2.append('function', 'getFarmDetail');
              f2.append('farmId', this.form.farmId);
              var r2 = await execute(f2, flocker);
              if (r2.data && r2.data.success && r2.data.data && r2.data.data.farm) {
                var farm = r2.data.data.farm;
                if (farm.county_id) this.form.countyId = farm.county_id;
                if (farm.latitude) this.form.latitude = farm.latitude;
                if (farm.longitude) this.form.longitude = farm.longitude;
                if (farm.county_name) this.form.locationDescription = farm.county_name;
              }
            } catch(e2) {}
          }

          // Load batch photos
          var batchPhotos = r.data.data.photos || [];
          if (batchPhotos.length > 0) {
            var uploads_base = typeof uploads !== 'undefined' ? uploads : '';
            batchPhotos.forEach(function(p) {
              if (p.photo_url) {
                self.photos.push({
                  preview: p.photo_url.startsWith('http') ? p.photo_url : (uploads_base + String(p.photo_url).replace('uploads/', '')),
                  file: null,
                  fromAnimal: true,
                  animalPhotoUrl: p.photo_url
                });
              }
            });
          } else if (b.primary_photo) {
            var uploads_base = typeof uploads !== 'undefined' ? uploads : '';
            self.photos.push({
              preview: b.primary_photo.startsWith('http') ? b.primary_photo : (uploads_base + String(b.primary_photo).replace('uploads/', '')),
              file: null,
              fromAnimal: true,
              animalPhotoUrl: b.primary_photo
            });
          }
        }
      } catch(e) {
        console.error('Failed to prefill from batch:', e);
      }
    },
    async fetchSpecies() {
      try {
        var response = await executeJson({ function: "getAnimalSpecies" }, flocker);
        if (response.data.success) this.speciesList = response.data.data.species || [];
      } catch (e) { console.error('Error fetching species:', e); }
    },

    async fetchBreeds() {
      if (!this.form.speciesId) { this.breedsList = []; return; }
      try {
        var response = await executeJson({ function: "getAnimalBreeds", speciesId: this.form.speciesId }, flocker);
        if (response.data.success) this.breedsList = response.data.data.breeds || [];
      } catch (e) { console.error('Error fetching breeds:', e); }
    },

    async fetchCounties() {
      try {
        var response = await executeJson({ function: "getCounties", countryCode: "KE" }, flocker);
        if (response.data.success) this.counties = response.data.data.counties || [];
      } catch (e) { console.error('Error fetching counties:', e); }
    },

    async fetchSubCounties() {
      if (!this.form.countyId) { this.subCounties = []; return; }
      try {
        var response = await executeJson({ function: "getSubCounties", countyId: this.form.countyId }, flocker);
        if (response.data.success) this.subCounties = response.data.data.subCounties || [];
      } catch (e) { console.error('Error fetching sub-counties:', e); }
    },

    async fetchFarms() {
      var userId = sessionStorage.getItem('userId');
      if (!userId) return;
      try {
        var response = await executeJson({ function: "getMyFarms", ownerId: userId }, flocker);
        if (response.data.success) this.farms = response.data.data.farms || [];
      } catch (e) { console.error('Error fetching farms:', e); }
    },

    onSpeciesChange: function() {
      this.form.breedId = '';
      this.fetchBreeds();
    },

    onCountyChange: function() {
      this.form.subCountyId = '';
      this.fetchSubCounties();
    },

    // ============ PHOTOS ============

    handlePhotoUpload: function(event) {
      var self = this;
      var files = Array.from(event.target.files || []);
      var remaining = 6 - self.photos.length;
      var toProcess = files.slice(0, remaining).filter(function(f) { return f.type.startsWith('image/'); });
      if (toProcess.length === 0) return;

      self.compressing = true;
      var processed = 0;

      toProcess.forEach(function(file) {
        self.compressImage(file, 1200, 0.8, function(compressedFile, previewUrl) {
          self.photos.push({ file: compressedFile, preview: previewUrl, originalSize: file.size });
          processed++;
          if (processed >= toProcess.length) self.compressing = false;
        });
      });

      event.target.value = '';
    },

    compressImage: function(file, maxDim, quality, callback) {
      var reader = new FileReader();
      reader.onload = function(e) {
        var img = new Image();
        img.onload = function() {
          var w = img.width;
          var h = img.height;

          // Scale down if larger than maxDim
          if (w > maxDim || h > maxDim) {
            if (w > h) { h = Math.round(h * maxDim / w); w = maxDim; }
            else { w = Math.round(w * maxDim / h); h = maxDim; }
          }

          var canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          var ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);

          // Convert to JPEG blob
          canvas.toBlob(function(blob) {
            if (!blob) { callback(file, e.target.result); return; }

            // If still over 2MB, reduce quality further
            if (blob.size > 2 * 1024 * 1024) {
              canvas.toBlob(function(blob2) {
                var finalBlob = blob2 || blob;
                var compressed = new File([finalBlob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' });
                callback(compressed, URL.createObjectURL(compressed));
              }, 'image/jpeg', 0.6);
            } else {
              var compressed = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' });
              callback(compressed, URL.createObjectURL(compressed));
            }
          }, 'image/jpeg', quality);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    formatFileSize: function(bytes) {
      if (!bytes) return '0 KB';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },

    removePhoto: function(index) {
      this.photos.splice(index, 1);
    },

    // ============ SUBMIT ============

    async submitListing() {
      var userId = sessionStorage.getItem('userId');
      if (!userId) { this.$router.push('/login'); return; }

      this.submitting = true;
      this.submitError = '';

      try {
        var params = {
          function: "createListing",
          sellerId: userId,
          listingType: this.form.listingType,
          title: this.form.title,
          description: this.form.description,
          speciesId: this.form.speciesId,
          price: this.form.price,
          priceType: this.form.priceType,
          countyId: this.form.countyId
        };

        if (this.form.breedId) params.breedId = this.form.breedId;
        if (this.form.farmId) params.farmId = this.form.farmId;
        if (this.form.animalId) params.animalId = this.form.animalId;
        if (this.form.batchId) params.batchId = this.form.batchId;
        if (this.form.sex) params.sex = this.form.sex;
        if (this.form.ageMonths) params.ageMonths = this.form.ageMonths;
        if (this.form.weightKg) params.weightKg = this.form.weightKg;
        if (this.form.totalQuantity) params.totalQuantity = this.form.totalQuantity;
        if (this.form.availableQuantity) params.availableQuantity = this.form.availableQuantity;
        if (this.form.minOrderQuantity) params.minOrderQuantity = this.form.minOrderQuantity;
        if (this.form.avgWeightKg) params.avgWeightKg = this.form.avgWeightKg;
        if (this.form.locationDescription) params.locationDescription = this.form.locationDescription;
        if (this.form.latitude) params.latitude = this.form.latitude;
        if (this.form.longitude) params.longitude = this.form.longitude;
        if (this.form.offersDelivery) {
          params.offersDelivery = '1';
          params.deliveryBaseFee = this.form.deliveryBaseFee || '0';
          params.deliveryPerKm = this.form.deliveryPerKm || '0';
          params.deliveryMaxKm = this.form.deliveryMaxKm || '0';
          if (this.form.deliveryNotes) params.deliveryNotes = this.form.deliveryNotes;
        }

        // Build FormData for file uploads
        var formData = new FormData();
        Object.keys(params).forEach(function(key) {
          formData.append(key, params[key]);
        });
        var photoIdx = 0;
        var animalPhotoUrls = [];
        this.photos.forEach(function(ph) {
          if (ph.file) {
            formData.append('photo' + photoIdx, ph.file);
            photoIdx++;
          } else if (ph.fromAnimal && ph.animalPhotoUrl) {
            animalPhotoUrls.push(ph.animalPhotoUrl);
          }
        });
        if (animalPhotoUrls.length > 0) {
          formData.append('animalPhotoUrls', JSON.stringify(animalPhotoUrls));
        }

        var response = await execute(formData, flocker);
        if (response.data.success) {
          var listingId = response.data.data.listingId;
          this.$router.push('/marketplace/listing/' + listingId);
        } else {
          this.submitError = response.data.message || 'Failed to create listing. Please try again.';
        }
      } catch (e) {
        console.error('Error creating listing:', e);
        this.submitError = 'Something went wrong. Please try again.';
      } finally {
        this.submitting = false;
      }
    },

    // ============ HELPERS ============

    getSpeciesName: function() {
      var self = this;
      var sp = this.speciesList.find(function(s) { return String(s.id) === String(self.form.speciesId); });
      return sp ? sp.name : '';
    },
    getBreedName: function() {
      var self = this;
      var br = this.breedsList.find(function(b) { return String(b.id) === String(self.form.breedId); });
      return br ? br.name : '';
    },
    getCountyName: function() {
      var self = this;
      var c = this.counties.find(function(co) { return String(co.id) === String(self.form.countyId); });
      return c ? c.name : '';
    },
    formatPrice: function(amount) {
      if (!amount) return '0';
      return Number(amount).toLocaleString('en-KE');
    }
  }
};
</script>
